import { observer } from "mobx-react-lite";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { useStore } from "store";
import MenuOrderModal from "./MenuOrderModal";
import { ICartItem, PaymentMethod, paymentMethods } from "types/cart";
import { useToggleState } from "hooks";
import { FaChevronUp } from "react-icons/fa";
import { BsTrashFill } from "react-icons/bs";
import { useQueryClient } from "react-query";
import { CgSpinnerAlt } from "react-icons/cg";
import toast from "react-hot-toast";
import { roundToNearestHalf } from "utils/numberHelpers";
import { ConfirmationModal } from "shared-widgets";
import { UseToggleStateReturn } from "hooks/useToggleState";
import SignaturePad from "react-signature-canvas";
import { queryKeys } from "queries";

const CartItem: FC<{ cartItem: ICartItem; viewCartItemModalToggler: UseToggleStateReturn; deleteCartItemModalToggler: UseToggleStateReturn }> =
	observer(({ cartItem, viewCartItemModalToggler, deleteCartItemModalToggler }) => {
		const { cart } = useStore();

		const totalPrice = (
			cartItem.quantity *
			(cart.priceMultiplier * (cartItem.price + Object.values(cartItem.options || []).reduce((prev, curr) => curr.price + prev, 0)))
		).toFixed(2);

		return (
			<>
				<div className="px-4 py-2 flex flex-col bg-primary rounded-lg" onClick={viewCartItemModalToggler.on}>
					<div className="flex justify-between">
						<span className="font-medium">{cartItem.menuItem.titleEn}</span>
						<span className="flex items-center gap-x-3">
							x{cartItem.quantity}
							<BsTrashFill
								className="text-red-400 mb-0.5 cursor-pointer"
								onClick={(e) => {
									cart.setExpandedItem(cartItem);
									deleteCartItemModalToggler.on();
									e.stopPropagation();
								}}
							/>
						</span>
					</div>
					<div className="flex justify-between mt-2 text-secondary items-end">
						<div className="flex flex-col items-start gap-y-1 w-full">
							{Object.entries(cartItem.options).map(([optionName, choice]) => (
								<span className="text-sm rounded bg-gray-primary text-secondary px-1.5" key={optionName + choice.id}>
									{optionName.replace(":" + choice.name, "")}: {choice.name} [
									{roundToNearestHalf(cart.priceMultiplier * choice.price).toFixed(2)}pts]
								</span>
							))}
							<div className="w-full h-[1px] bg-secondary mt-2"></div>
							<div className="text-white w-full text-right">Price: {totalPrice} pts</div>
						</div>
					</div>
				</div>
			</>
		);
	});

const DiscountRateInput = observer(() => {
	const { cart } = useStore();
	return (
		<>
			<div className="border border-primary rounded-lg flex justify-between text-white px-2 ml-1 mr-2.5 py-2 mb-1 items-center">
				<span>Discount</span>
				<span className="gap-x-1 flex items-center">
					<button className="bg-gray-500 text-white px-1 py-0.5 rounded" onClick={cart.resetDiscount}>
						Reset
					</button>
					<input
						className="w-24 py-0.5 rounded border-gray-500 border px-1"
						value={cart.discountRate}
						type="number"
						onChange={(e) => cart.setDiscountRate(Number(e.target.value))}
					/>
					%
				</span>
			</div>
			<div className="border border-primary rounded-lg flex justify-between text-white px-2 ml-1 mr-2.5 py-2">
				<span>Total</span>
				<span>{cart.totalPrice.toFixed(2)} pts</span>
			</div>
		</>
	);
});

const Cart: FC = observer(() => {
	const { cart } = useStore();
	const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(paymentMethods["Genius Points"]);
	const dropdownToggle = useToggleState(false);
	const viewCartItemModalToggler = useToggleState(false);
	const deleteCartItemModalToggler = useToggleState(false);
	const submitOrderModalToggler = useToggleState(false);
	const signatureRef = useRef<SignaturePad>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const queryClient = useQueryClient();

	const paymentOptions = useMemo(() => {
		return Object.keys(paymentMethods) as PaymentMethod[];
	}, []);

	const selectItem = useCallback((paymentMethodOption: PaymentMethod) => {
		setPaymentMethod(paymentMethodOption);
		dropdownToggle.off();
	}, []);

	// useEffect(() => {
	// 	let intervalId = setInterval(() => console.log(), 500);
	// 	return () => clearInterval(intervalId);
	// }, []);

	const submitOrder = () => {
		setIsLoading(true);
		const signatureImg = signatureRef.current?.getTrimmedCanvas().toDataURL();
		return toast
			.promise(cart.submitTransaction(paymentMethod, signatureImg), {
				loading: "Placing Order",
				success: "Order Placed",
				error: (err) => {
					return err.response ? JSON.stringify(err.response?.data?.detail) : err.message;
				},
			})
			.then((data) => {
				queryClient.invalidateQueries([queryKeys.USER]);
			})
			.finally(() => {
				setIsLoading(false);
				submitOrderModalToggler.off();
			});
	};

	return (
		<>
			{cart.expandedItem && (
				<MenuOrderModal
					item={cart.expandedItem?.menuItem}
					defaultCartItem={cart.expandedItem}
					editMode={true}
					toggle={viewCartItemModalToggler}
				/>
			)}
			<ConfirmationModal
				body={<span>Confirm remove {cart.expandedItem?.menuItem.titleEn}?</span>}
				modalToggle={deleteCartItemModalToggler}
				onConfirm={() => cart.remove(cart.expandedItem?.id!)}
			/>
			<ConfirmationModal
				body={
					!cart.isGuestOrder ? (
						<div className="relative">
							<SignaturePad penColor="black" ref={signatureRef} canvasProps={{ className: "bg-white rounded w-80 h-80" }} />
							<button
								className="absolute bottom-2 right-2 text-white bg-primary rounded px-2 py-0.5"
								onClick={signatureRef.current?.clear}
							>
								Clear
							</button>
						</div>
					) : (
						"Confirm order?"
					)
				}
				modalToggle={submitOrderModalToggler}
				validation={() => cart.isGuestOrder || !signatureRef.current?.isEmpty()}
				validationMessage="Signature is required"
				onConfirm={submitOrder}
				disabled={isLoading}
			/>
			<div className="flex flex-col flex-1">
				<div className="flex flex-col flex-1 h-full max-h-full overflow-y-scroll gap-y-0.5 mb-2 px-1.5 py-1">
					{cart.items.map((cartItem) => (
						<CartItem
							cartItem={cartItem}
							key={cartItem.id}
							viewCartItemModalToggler={viewCartItemModalToggler}
							deleteCartItemModalToggler={deleteCartItemModalToggler}
						/>
					))}
				</div>
				<DiscountRateInput />
			</div>
			<div className="flex items-center gap-x-1 mt-1 relative ml-1 mr-2.5">
				<div className="whitespace-nowrap flex-1 border rounded-l-lg border-primary relative grid grid-cols-10">
					<div
						className={`bottom-full absolute flex flex-col w-full bg-black border max-h-[60vh] overflow-y-auto border-primary shadow-secondary shadow-sm border-b-0 rounded-lg mb-1 ${
							!dropdownToggle.isToggled && "hidden"
						}`}
					>
						{paymentOptions.map((paymentMethodOption, index) => (
							<div
								className={`py-2 px-2 hover:bg-opacity-50 hover:bg-secondary ${index === 0 ? "rounded-t-lg" : ""} ${
									index === paymentOptions.length - 1 ? "rounded-b-lg" : ""
								}  ${paymentMethodOption === paymentMethod ? "bg-secondary" : ""}`}
								onClick={() => selectItem(paymentMethodOption)}
								key={paymentMethodOption}
							>
								{paymentMethodOption}
							</div>
						))}
					</div>
					<span className="col-span-8 py-2 cursor-pointer px-2" onClick={dropdownToggle.toggle}>
						{paymentMethod}
					</span>
					<span className="col-span-2 flex items-center justify-center bg-primary py-3.5 cursor-pointer" onClick={dropdownToggle.toggle}>
						<FaChevronUp className="w-3 h-3" />
					</span>
				</div>
				<button
					className="w-32 py-2 bg-primary border border-primary rounded-r-md font-medium flex items-center justify-center disabled:bg-opacity-50 disabled:border-opacity-50"
					onClick={() => {
						if (cart.isGuestOrder) return submitOrderModalToggler.on();
						return cart.orderingUser ? submitOrderModalToggler.on() : toast.error("Select a user");
					}}
					disabled={isLoading}
					type="button"
				>
					{isLoading ? <CgSpinnerAlt className="animate-spin h-6" /> : "Checkout"}
				</button>
			</div>
		</>
	);
});

export default Cart;
